/* =========================================================================
   ELYSIAN SLEEP - storefront behaviour
   Vanilla, no dependencies. Every scroll-driven effect uses
   IntersectionObserver; there are no scroll event listeners anywhere.
   ========================================================================= */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function $(sel, root) { return (root || document).querySelector(sel); }
  function $$(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  /* ---------------------------------------------------------------------
     Money. Formatted client-side so the price can follow the size choice
     without a round trip. Format string comes from the theme.
     --------------------------------------------------------------------- */
  var moneyFormat = (document.documentElement.getAttribute('data-es-money') || '£{{amount}}');

  function formatMoney(cents) {
    var n = (cents / 100).toFixed(2);
    var parts = n.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    var out = whole + '.' + parts[1];
    if (moneyFormat.indexOf('amount_no_decimals') > -1 && parts[1] === '00') out = whole;
    return moneyFormat.replace(/\{\{\s*amount[^}]*\}\}/, out);
  }

  /* =====================================================================
     NAVIGATION
     ===================================================================== */

  function initMegaMenu() {
    var items = $$('[data-es-mega]');
    if (!items.length) return;
    var openItem = null;
    var closeTimer = null;

    function close(item) {
      if (!item) return;
      var btn = $('.es-nav__link', item);
      var panel = $('.es-mega', item);
      if (btn) btn.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (openItem === item) openItem = null;
    }

    function open(item) {
      if (openItem && openItem !== item) close(openItem);
      var btn = $('.es-nav__link', item);
      var panel = $('.es-mega', item);
      if (btn) btn.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
      openItem = item;
    }

    items.forEach(function (item) {
      var btn = $('.es-nav__link', item);

      item.addEventListener('mouseenter', function () {
        clearTimeout(closeTimer);
        open(item);
      });
      item.addEventListener('mouseleave', function () {
        closeTimer = setTimeout(function () { close(item); }, 140);
      });

      if (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          if (btn.getAttribute('aria-expanded') === 'true') close(item); else open(item);
        });
      }

      // Close when focus leaves the whole item, so keyboard users are not trapped.
      item.addEventListener('focusout', function (e) {
        if (!item.contains(e.relatedTarget)) close(item);
      });
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && openItem) {
        var btn = $('.es-nav__link', openItem);
        close(openItem);
        if (btn) btn.focus();
      }
    });
  }

  function initDrawer() {
    var drawer = $('#es-drawer');
    if (!drawer) return;
    var opener = $('[data-es-drawer-open]');
    var closer = $('[data-es-drawer-close]');
    var lastFocus = null;

    function setOpen(open) {
      drawer.setAttribute('data-open', open ? 'true' : 'false');
      drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
      document.documentElement.style.overflow = open ? 'hidden' : '';
      if (opener) opener.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) {
        lastFocus = document.activeElement;
        if (closer) closer.focus();
      } else if (lastFocus) {
        lastFocus.focus();
      }
    }

    if (opener) opener.addEventListener('click', function () { setOpen(true); });
    if (closer) closer.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('data-open') === 'true') setOpen(false);
    });

    // Simple focus trap while the drawer is open.
    drawer.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || drawer.getAttribute('data-open') !== 'true') return;
      var focusable = $$('a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])', drawer)
        .filter(function (el) { return el.offsetParent !== null; });
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });

    $$('[data-es-acc]', drawer).forEach(function (btn) {
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var panel = btn.nextElementSibling;
        if (panel) panel.hidden = expanded;
      });
    });
  }

  /* =====================================================================
     STICKY HEADER OFFSET
     #header-group is sticky and pulled up by the announcement bar's height,
     so the announcement scrolls away and the header lands flush at the top.
     The height is measured rather than hard-coded because the announcement
     wraps to two lines on narrow screens.
     ===================================================================== */

  function initStickyHeader() {
    var group = document.getElementById('header-group');
    if (!group) return;
    var announce = group.querySelector('.es-announce');

    function measure() {
      var h = announce ? Math.round(announce.getBoundingClientRect().height) : 0;
      group.style.setProperty('--es-announce-h', h + 'px');
      // Anything that pins below the header needs to clear it.
      var header = group.querySelector('.es-header');
      if (header) {
        document.documentElement.style.setProperty(
          '--es-header-h',
          Math.round(header.getBoundingClientRect().height) + 'px'
        );
      }
    }

    measure();
    if ('ResizeObserver' in window) {
      var ro = new ResizeObserver(measure);
      if (announce) ro.observe(announce);
      var hdr = group.querySelector('.es-header');
      if (hdr) ro.observe(hdr);
    } else {
      window.addEventListener('resize', measure);
    }
    // Webfonts change the announcement's line count, so measure again after.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
  }

  /* =====================================================================
     SCROLL REVEAL
     Motivated: sequences each band as its own beat instead of the whole
     page landing at once. Static under reduced motion.
     ===================================================================== */

  function initReveal() {
    var els = $$('.es-reveal');
    if (!els.length) return;
    if (reduceMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.setAttribute('data-shown', 'true'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.setAttribute('data-shown', 'true');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* =====================================================================
     PRODUCT GALLERY
     ===================================================================== */

  function initGallery() {
    $$('[data-es-gallery]').forEach(function (gallery) {
      // Shopify's image_tag does not pass arbitrary data attributes through, so
      // fall back to the structural selector for real product images.
      var main = $('[data-es-gallery-main]', gallery) || $('.es-gallery__main img', gallery);
      if (!main) return;
      $$('[data-es-gallery-thumb]', gallery).forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          $$('[data-es-gallery-thumb]', gallery).forEach(function (t) {
            t.setAttribute('aria-current', 'false');
          });
          thumb.setAttribute('aria-current', 'true');
          main.src = thumb.getAttribute('data-full') || thumb.querySelector('img').src;
          main.alt = thumb.getAttribute('data-alt') || '';
        });
      });
    });
  }

  /* =====================================================================
     PRODUCT CONFIGURATOR
     Size is a Shopify variant, so it sets the price. Fabric is a line item
     property: it does not change price and would otherwise need one variant
     per size-and-fabric pair, which no catalogue wants to maintain.
     ===================================================================== */

  function initConfigurator() {
    $$('[data-es-configurator]').forEach(function (form) {
      var variantInput = $('[data-es-variant-id]', form);
      var addBtn = $('[data-es-add]', form);
      var buyBtn = $('[data-es-buy]', form);
      var errorEl = $('[data-es-error]', form);
      var summarySize = $('[data-es-summary-size]', form);
      var summaryFabric = $('[data-es-summary-fabric]', form);
      var summaryPrice = $('[data-es-summary-price]', form);
      var totalEl = $('[data-es-total]', form);
      var fabricInput = $('[data-es-fabric-value]', form);
      var fabricPreview = $$('[data-es-fabric-preview]', form);
      var requiresFabric = form.getAttribute('data-requires-fabric') === 'true';
      var summaryStorage = $('[data-es-summary-storage]', form);
      var storageHint = $('[data-es-storage-hint]', form);
      var storageInputs = $$('[data-es-storage]', form);

      // A storage bed has two options, size and storage, so its variant is
      // found from the pair rather than read off the size card.
      var combos = $$('[data-es-combos] [data-size]', form).map(function (el) {
        return {
          size: el.getAttribute('data-size'),
          storage: el.getAttribute('data-storage'),
          id: el.getAttribute('data-id'),
          price: parseInt(el.getAttribute('data-price'), 10),
          available: el.getAttribute('data-available') === 'true'
        };
      });
      var comboMode = combos.length > 0;

      var state = {
        price: null, sizeLabel: null, sizeValue: null,
        storageValue: null, storageLabel: null, fabricLabel: null, notice: null
      };

      function render() {
        var priceText = state.price === null ? '' : formatMoney(state.price);

        if (summarySize) {
          summarySize.innerHTML = state.sizeLabel
            ? 'Size: <strong>' + state.sizeLabel + '</strong>'
            : 'Size: not selected';
          summarySize.setAttribute('data-empty', state.sizeLabel ? 'false' : 'true');
        }
        if (summaryFabric) {
          summaryFabric.innerHTML = state.fabricLabel
            ? 'Fabric: <strong>' + state.fabricLabel + '</strong>'
            : 'Fabric: not selected';
          summaryFabric.setAttribute('data-empty', state.fabricLabel ? 'false' : 'true');
        }
        if (summaryStorage) {
          summaryStorage.innerHTML = state.storageLabel
            ? 'Storage: <strong>' + state.storageLabel + '</strong>'
            : 'Storage: not selected';
          summaryStorage.setAttribute('data-empty', state.storageLabel ? 'false' : 'true');
        }
        if (summaryPrice) summaryPrice.textContent = priceText;
        if (totalEl) {
          totalEl.textContent = priceText || (comboMode && state.sizeLabel ? 'Select storage' : 'Select a size');
          totalEl.classList.toggle('is-empty', !priceText);
        }

        // aria-disabled, not the disabled attribute: a disabled button
        // swallows the click, so the customer never finds out what is missing.
        var ready = state.price !== null && (!requiresFabric || !!state.fabricLabel);
        [addBtn, buyBtn].forEach(function (btn) {
          if (!btn) return;
          btn.setAttribute('aria-disabled', ready ? 'false' : 'true');
        });
        if (addBtn) {
          var label = $('[data-es-add-label]', addBtn) || addBtn;
          label.textContent = ready ? 'Add to basket · ' + priceText : 'Add to basket';
        }
        if (errorEl) errorEl.textContent = state.notice || '';
        state.notice = null;
      }

      // --- Storage ------------------------------------------------------
      function comboFor(size, storage) {
        for (var i = 0; i < combos.length; i++) {
          if (combos[i].size === size && combos[i].storage === storage) return combos[i];
        }
        return null;
      }

      function resolveCombo() {
        var c = state.sizeValue && state.storageValue ? comboFor(state.sizeValue, state.storageValue) : null;
        var ok = !!(c && c.available);
        state.price = ok ? c.price : null;
        if (variantInput) variantInput.value = ok ? c.id : '';
      }

      // Price every storage option for the chosen size, and switch off the
      // ones not made in it. Before a size is chosen the first size is used
      // as a guide and nothing is switched off.
      function refreshStorage() {
        var sizeInputs = $$('[data-es-size]', form);
        var guide = sizeInputs.filter(function (i) { return !i.disabled; })[0] || sizeInputs[0];
        var size = state.sizeValue || (guide && guide.value);
        var sizeLabel = state.sizeLabel || (guide && guide.getAttribute('data-label')) || '';
        var base = null;
        combos.forEach(function (c) {
          if (c.size === size && c.available && (base === null || c.price < base)) base = c.price;
        });
        storageInputs.forEach(function (input) {
          var c = comboFor(size, input.value);
          var ok = !!(c && c.available);
          var card = input.closest('label');
          var delta = card && $('[data-es-storage-delta]', card);
          if (state.sizeValue) {
            input.disabled = !ok;
            if (!ok && input.checked) {
              input.checked = false;
              state.notice = state.storageLabel + (c ? ' is sold out in ' : ' is not made in ') + sizeLabel + '. Please choose another storage option.';
              state.storageValue = null;
              state.storageLabel = null;
            }
          }
          if (delta) {
            if (!c) delta.textContent = 'Not made in ' + sizeLabel;
            else if (!c.available) delta.textContent = 'Sold out in ' + sizeLabel;
            else if (c.price === base) delta.textContent = 'Included';
            else delta.textContent = '+ ' + formatMoney(c.price - base);
          }
        });
        if (storageHint && sizeLabel) storageHint.textContent = 'Prices for ' + sizeLabel;
      }

      storageInputs.forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          state.storageValue = input.value;
          state.storageLabel = input.getAttribute('data-label');
          resolveCombo();
          render();
        });
        if (input.checked) {
          state.storageValue = input.value;
          state.storageLabel = input.getAttribute('data-label');
        }
      });

      // --- Size ---------------------------------------------------------
      function chooseSize(input) {
        state.sizeLabel = input.getAttribute('data-label');
        if (comboMode) {
          state.sizeValue = input.value;
          refreshStorage();
          resolveCombo();
          return;
        }
        state.price = parseInt(input.getAttribute('data-price'), 10);
        if (variantInput) variantInput.value = input.value;
      }

      $$('[data-es-size]', form).forEach(function (input) {
        input.addEventListener('change', function () {
          if (!input.checked) return;
          chooseSize(input);
          render();
        });
        if (input.checked) chooseSize(input);
      });
      if (comboMode && !state.sizeValue) refreshStorage();

      // --- Fabric -------------------------------------------------------
      // The inline grid and the dialog are separate radio groups, so both can
      // show a selection at once. This mirrors a choice made in either one
      // across to the other, and reveals the swatch inline when it was picked
      // from the dialog and sits past the preview count.
      function selectFabric(input, mirror) {
        state.fabricLabel = input.getAttribute('data-label');
        if (fabricInput) fabricInput.value = state.fabricLabel;

        var handle = input.getAttribute('data-handle');
        var swatchUrl = input.getAttribute('data-swatch');
        var collection = input.getAttribute('data-collection');

        fabricPreview.forEach(function (el) {
          if (!swatchUrl) return;
          el.style.backgroundImage = 'url(' + swatchUrl + ')';
          el.style.backgroundSize = 'cover';
        });

        if (mirror !== false) {
          $$('[data-es-fabric]', form).forEach(function (other) {
            if (other === input) return;
            // Only touch the other group: setting checked on a radio in the
            // same group would clear the one the customer just clicked.
            if (other.name === input.name) return;
            if (other.getAttribute('data-handle') === handle) other.checked = true;
          });
        }

        // Make the choice visible on the page, not only in the dialog.
        $$('[data-es-fabric]', form).forEach(function (other) {
          if (other.getAttribute('data-handle') !== handle) return;
          var swatch = other.closest('.es-swatch');
          if (swatch) swatch.classList.remove('es-swatch--extra');
        });

        if (collection) showFabricTab(collection);
        refreshMoreCounts();

        var chosen = $('[data-es-fabric-chosen]', form);
        if (chosen) chosen.textContent = 'Chosen: ' + state.fabricLabel;

        render();
      }

      // Revealing a swatch makes the "+N more" tile's count wrong, so recount
      // what is still hidden in each collection.
      function refreshMoreCounts() {
        $$('[role="tabpanel"]', form).forEach(function (panel) {
          var tile = $('.es-swatch--more .es-swatch__chip span', panel);
          if (!tile) return;
          var hidden = $$('.es-swatch--extra', panel).length;
          if (hidden > 0) {
            tile.innerHTML = '+' + hidden + '<br>more';
          } else {
            tile.textContent = 'All shown';
          }
        });
      }

      // Bring the chosen fabric's collection to the front of the inline tabs.
      function showFabricTab(collection) {
        var tablist = $('[data-es-fabtabs]', form);
        if (!tablist) return;
        var tab = $$('[role="tab"]', tablist).filter(function (t) {
          return (t.getAttribute('aria-controls') || '').indexOf('-panel-' + collection) > -1;
        })[0];
        if (tab && tab.getAttribute('aria-selected') !== 'true') tab.click();
      }

      $$('[data-es-fabric]', form).forEach(function (input) {
        input.addEventListener('change', function () {
          if (input.checked) selectFabric(input);
        });
        if (input.checked) selectFabric(input, false);
      });

      // --- Submit -------------------------------------------------------
      form.addEventListener('submit', function (e) {
        var ready = state.price !== null && (!requiresFabric || !!state.fabricLabel);
        if (ready) return;
        e.preventDefault();
        var missing = !state.sizeLabel ? 'size' : (state.price === null ? 'storage' : 'fabric');
        if (errorEl) {
          errorEl.textContent = {
            size: 'Please choose a size to continue.',
            storage: 'Please choose a storage option to continue.',
            fabric: 'Please choose a fabric to continue.'
          }[missing];
        }
        var target = missing === 'size'
          ? $('[data-es-size]', form)
          : missing === 'storage'
            ? storageInputs.filter(function (i) { return !i.disabled; })[0]
            : $('[data-es-fabric]', form);
        if (target) {
          var box = target.closest('label') || target;
          box.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
          target.focus({ preventScroll: true });
        }
      });

      render();
    });
  }

  /* =====================================================================
     FABRIC DIALOG AND TABS
     ===================================================================== */

  function initFabricUI() {
    $$('[data-es-fabric-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dialog = document.getElementById(btn.getAttribute('data-es-fabric-open'));
        if (dialog && dialog.showModal) dialog.showModal();
      });
    });
    $$('[data-es-fabric-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var dialog = btn.closest('dialog');
        if (dialog) dialog.close();
      });
    });

    $$('[data-es-fabtabs]').forEach(function (tablist) {
      var tabs = $$('[role="tab"]', tablist);
      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) {
            t.setAttribute('aria-selected', 'false');
            var p = document.getElementById(t.getAttribute('aria-controls'));
            if (p) p.hidden = true;
          });
          tab.setAttribute('aria-selected', 'true');
          var panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = false;
        });
        tab.addEventListener('keydown', function (e) {
          var next = null;
          if (e.key === 'ArrowRight') next = tabs[(i + 1) % tabs.length];
          if (e.key === 'ArrowLeft') next = tabs[(i - 1 + tabs.length) % tabs.length];
          if (!next) return;
          e.preventDefault();
          next.focus();
          next.click();
        });
      });
    });
  }

  /* =====================================================================
     FABRIC SAMPLES
     Free, and capped. The cap is enforced here for feedback and again on
     the server side by the cart line quantity rules in the handover notes.
     ===================================================================== */

  function initSamples() {
    var root = $('[data-es-samples]');
    if (!root) return;
    var max = parseInt(root.getAttribute('data-max'), 10) || 5;
    var inputs = $$('[data-es-sample]', root);
    var countEl = $('[data-es-sample-count]', root);
    var dotsEl = $('[data-es-sample-dots]', root);
    var submit = $('[data-es-sample-submit]', root);
    var hintEl = $('[data-es-sample-hint]', root);

    function selected() {
      return inputs.filter(function (i) { return i.checked; });
    }

    function update() {
      var n = selected().length;
      var full = n >= max;

      root.setAttribute('data-full', full ? 'true' : 'false');
      inputs.forEach(function (i) { i.disabled = full && !i.checked; });

      if (countEl) countEl.textContent = n;
      if (dotsEl) {
        $$('.es-samples__dot', dotsEl).forEach(function (dot, i) {
          dot.setAttribute('data-filled', i < n ? 'true' : 'false');
        });
      }
      if (submit) {
        submit.setAttribute('aria-disabled', n === 0 ? 'true' : 'false');
        var label = $('[data-es-sample-label]', submit) || submit;
        label.textContent = n === 0
          ? 'Choose your samples'
          : 'Order ' + n + (n === 1 ? ' free sample' : ' free samples');
      }
      if (hintEl) {
        hintEl.textContent = full
          ? 'That is the maximum. Deselect one to swap it.'
          : 'Choose up to ' + max + '. Delivered free.';
      }
    }

    inputs.forEach(function (i) { i.addEventListener('change', update); });

    root.addEventListener('submit', function (e) {
      if (selected().length > 0) return;
      e.preventDefault();
      if (hintEl) hintEl.textContent = 'Choose at least one swatch to continue.';
      var first = inputs[0];
      if (first) {
        (first.closest('label') || first).scrollIntoView({
          behavior: reduceMotion ? 'auto' : 'smooth', block: 'center'
        });
        first.focus({ preventScroll: true });
      }
    });

    update();
  }

  /* =====================================================================
     ORDER TRACKER
     ===================================================================== */

  /* =====================================================================
     COLLECTION FILTERS
     The sidebar is always shown on wide screens. On narrow ones it is hidden
     until the Filter button asks for it.
     ===================================================================== */

  function initFilters() {
    $$('[data-es-filter-toggle]').forEach(function (btn) {
      var panel = document.getElementById(btn.getAttribute('aria-controls'));
      if (!panel) return;
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') !== 'true';
        btn.setAttribute('aria-expanded', String(open));
        panel.classList.toggle('es-facets--open', open);
      });
    });
  }

  function initTracker() {
    $$('[data-es-track]').forEach(function (track) {
      var stages = $$('.es-track__stage', track);
      var fill = $('.es-track__fill', track);
      if (!fill || stages.length < 2) return;
      var doneCount = stages.filter(function (s) {
        return s.getAttribute('data-state') !== 'todo';
      }).length;
      // The rail spans centre-of-first to centre-of-last, which is
      // (stages - 1) / stages of the full width.
      var span = (stages.length - 1) / stages.length * 100;
      var progress = doneCount <= 1 ? 0 : (doneCount - 1) / (stages.length - 1);
      if (window.matchMedia('(min-width: 700px)').matches) {
        fill.style.width = (progress * span) + '%';
      }
    });
  }

  /* =====================================================================
     CART COUNT
     Keeps the header badge in step after any add to cart on the page.
     ===================================================================== */

  function refreshCartCount() {
    fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart.js' : '/cart.js', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (!cart) return;
        $$('[data-es-cart-count]').forEach(function (el) { el.textContent = cart.item_count; });
      })
      .catch(function () { /* badge simply stays as rendered */ });
  }

  document.addEventListener('cart:updated', refreshCartCount);
  document.addEventListener('cart:refresh', refreshCartCount);

  /* ===================================================================== */

  function init() {
    initStickyHeader();
    initMegaMenu();
    initDrawer();
    initReveal();
    initGallery();
    initConfigurator();
    initFabricUI();
    initSamples();
    initTracker();
    initFilters();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor reloads sections without a full page load.
  document.addEventListener('shopify:section:load', init);
})();
